import React, { useRef, useState, useEffect } from "react";
import CommonSelect from "../../../core/common/commonSelect";
import { TableData } from "../../../core/data/interface";
import Table from "../../../core/common/dataTable/index";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { useAttendance } from "./hooks/useAttendance";
import { StudentAttendanceTypes } from "./types/attendanceTypes";
import { useCourse } from "../../configuration/course/hooks/useCourse";
import { useBatchTiming } from "../../configuration/batch-timing/hooks/useBatchTiming";
import { useSession } from "../../configuration/session/hooks/useSession";
import { useCenter } from "../../peoples/centers/hooks/useCenter";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { useSelector } from "react-redux";
import { selectUserRole } from "../../../core/data/slice/authSlice";

// Main student attendance management component
const StudentAttendance = () => {
  const routes = all_routes;
  const currentDate = new Date().toISOString().split("T")[0];
  const userRole = useSelector(selectUserRole);
  const isSuperAdmin = userRole === "superadmin";

  // Add state variables for pagination
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [search, setSearch] = useState("");

  // Modify hook usage with pagination params
  const {
    students,
    isLoading,
    selectedStudentKeys,
    bulkStatus,
    filterDate,
    paginationData,
    setSelectedStudentKeys,
    setBulkStatus,
    setFilterDate,
    handleOptionChange,
    handleBulkStatusChange,
    getStatusColor,
    handleFilterChange,
    handleApplyFilters,
    handleResetFilters,
    tempFilters,
    selectedCourseStages,
  } = useAttendance({
    page: pageNumber,
    size: recordsPerPage,
    search: search,
  });

  // Add pagination handlers
  const handlePageChange = (page: number, pageSize?: number) => {
    setPageNumber(page);
    if (pageSize) setRecordsPerPage(pageSize);
  };

  const handleSearchChange = (text: string) => {
    setSearch(text);
    setPageNumber(1);
  };

  const { courses } = useCourse({ size: 100 }).getCourseState();
  const { batchTimings } = useBatchTiming({ size: 100 });
  const { getSessionState } = useSession({ size: 100 });
  const { sessions } = getSessionState();
  const { getCenterState } = useCenter({
    size: 100,
    skip: userRole !== "superadmin",
  });
  const { centers } = getCenterState();

  const courseOptions = courses.map((course) => ({
    value: course.id.toString(),
    label: course.name,
  }));

  const batchOptions = batchTimings.map((batch) => ({
    value: batch.id.toString(),
    label: `${batch.name} (${batch.start_time} - ${batch.end_time})`,
  }));

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Deactive" },
  ];

  const sessionOptions = sessions.map((session) => ({
    value: session.id.toString(),
    label: session.name,
  }));

  const stageOptions = selectedCourseStages.map((stage: any) => ({
    value: stage.id.toString(),
    label: stage.name,
  }));

  const centerOptions = centers.map((center: any) => ({
    value: center.id.toString(),
    label: center.name,
  }));

  const findOptionByValue = (options: any[], value: string | undefined) => {
    return options.find((option) => option.value === value);
  };

  // Table column configuration with sorting and custom rendering
  const columns = [
    {
      title: "Student ID",
      dataIndex: "studentId",
      render: (text: string, record: StudentAttendanceTypes) => (
        <Link
          to={routes.studentAttendanceDetail.replace(
            ":id",
            record.student.id.toString()
          )}
          className="link-primary"
        >
          {record.studentId}
        </Link>
      ),
      sorter: false,
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (text: string, record: StudentAttendanceTypes) => (
        <div className="d-flex align-items-center">
          <Link to="#" className="avatar avatar-md">
            {record.img.includes("get-thumbnail") ? (
              <img
                src={record.img}
                className="img-fluid rounded-circle"
                alt={record.name}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  const target = e.target as HTMLImageElement;
                  target.src =
                    record.gender?.toLowerCase() === "female"
                      ? "/assets/img/profiles/default-female.png"
                      : "/assets/img/profiles/default-male.png";
                }}
              />
            ) : (
              <ImageWithBasePath
                src={
                  record.gender?.toLowerCase() === "female"
                    ? "assets/img/profiles/default-female.png"
                    : "assets/img/profiles/default-male.png"
                }
                className="img-fluid rounded-circle"
                alt={record.name}
              />
            )}
          </Link>
          <div className="ms-2">
            <Link
              to={routes.studentAttendanceDetail.replace(
                ":id",
                record.student.id.toString()
              )}
              className="link-primary"
            >
              {record.name}
            </Link>
          </div>
        </div>
      ),
      sorter: false,
    },
    {
      title: "Course",
      dataIndex: "course",
      sorter: false,
    },
    {
      title: "Level",
      dataIndex: "level",
      sorter: false,
    },
    // Add center column conditionally for superadmin
    ...(isSuperAdmin
      ? [
        {
          title: "Center",
          dataIndex: ["student", "center", "name"],
          render: (_: string, record: StudentAttendanceTypes) => (
            <span>{record.student?.center?.name || "-"}</span>
          ),
          sorter: false,
        },
      ]
      : []),
    {
      title: "Attendance Status",
      dataIndex: "attendanceStatus",
      render: (text: string, record: StudentAttendanceTypes) => {
        const statusColor = getStatusColor(record.attendanceStatus);
        const isPastDate = filterDate < currentDate;
        const isStatusAvailable = !!record.attendanceStatus;

        return (
          <select
            className={`form-select ${statusColor}`}
            value={record.attendanceStatus.toLowerCase()}
            onChange={(e) => handleOptionChange(record.key, e.target.value)}
            disabled={isPastDate && isStatusAvailable}
            style={
              isPastDate && isStatusAvailable
                ? {
                  cursor: "not-allowed",
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                  backgroundImage: "none",
                }
                : {}
            }
          >
            <option className="text-muted" value="">
              Select Status
            </option>
            <option className="text-muted" value="present">
              Present
            </option>
            <option className="text-muted" value="absent">
              Absent
            </option>
            <option className="text-muted" value="leave">
              Leave
            </option>
            <option className="text-muted" value="ncns">
              NCNS
            </option>
            <option className="text-muted" value="dropout">
              Dropout
            </option>
          </select>
        );
      },
      sorter: false,
    },
  ];

  // Filter handling logic
  const dropdownMenuRef = useRef<HTMLDivElement>(null);

  /**
   * Applies selected filters and closes dropdown
   * e - Mouse event from apply button
   */
  const handleApplyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show");
    }
    console.log("Applying filters..."); // TODO: Implement actual filter logic
  };

  const getCheckboxProps = (record: StudentAttendanceTypes) => {
    const existingRecord = students.find(
      (s) => s.key === record.key && s.attendanceDate === filterDate
    );

    return {
      disabled:
        !!existingRecord?.attendanceStatus && filterDate !== currentDate,
    };
  };

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header with breadcrumb navigation */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Student Attendance</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Student Attendance
                  </li>
                </ol>
              </nav>
            </div>
            {/* <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption />
            </div> */}
          </div>
          {/* /Page Header */}
          {/* Bulk Actions Bar */}
          {selectedStudentKeys.length > 0 && (
            <div className="bulk-actions-bar mb-3 p-3 bg-light rounded">
              <div className="d-flex flex-sm-row flex-column align-items-sm-center gap-2 gap-sm-3">
                <span className="text-nowrap">
                  {selectedStudentKeys.length} Students Selected
                </span>
                <select
                  className="form-select w-auto w-sm-100"
                  value={bulkStatus}
                  onChange={(e) => setBulkStatus(e.target.value)}
                >
                  <option value="">Select</option>
                  <option value="present">Mark as Present</option>
                  <option value="absent">Mark as Absent</option>
                  <option value="tardy">Mark as Tardy</option>
                  <option value="leave">Mark as Leave</option>
                </select>
                <button
                  className="btn btn-primary text-nowrap"
                  onClick={handleBulkStatusChange}
                >
                  <span className="d-none d-sm-inline">Apply to Selected</span>
                  <span className="d-inline d-sm-none">Apply to Selected</span>
                </button>
              </div>
            </div>
          )}
          {/* Interactive filter controls */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Student Attendance List</h4>
              <div className="d-flex align-items-center flex-wrap">
                {/* Replaced PredefinedDateRanges with date input */}
                <div className="input-icon-start mb-3 me-2 position-relative">
                  <input
                    type="date"
                    className="form-control"
                    value={filterDate}
                    max={currentDate} // Prevent future dates
                    onChange={(e) => {
                      setFilterDate(e.target.value);
                      setPageNumber(1);
                    }}
                  />
                </div>

                {/* Filter dropdown with form controls */}
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-filter me-2" />
                    Filter
                  </Link>
                  <div
                    className="dropdown-menu drop-width"
                    ref={dropdownMenuRef}
                  >
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 border-bottom">
                        <div className="row">
                          {isSuperAdmin && (
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">Center</label>
                                <CommonSelect
                                  className="select"
                                  options={centerOptions}
                                  value={findOptionByValue(
                                    centerOptions,
                                    tempFilters.center_id?.toString()
                                  )}
                                  onChange={(value) =>
                                    handleFilterChange("center_id", value)
                                  }
                                />
                              </div>
                            </div>
                          )}
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Session</label>
                              <CommonSelect
                                className="select"
                                options={sessionOptions}
                                value={findOptionByValue(
                                  sessionOptions,
                                  tempFilters.session_id?.toString()
                                )}
                                onChange={(value) =>
                                  handleFilterChange("session_id", value)
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Course</label>
                              <CommonSelect
                                className="select"
                                options={courseOptions}
                                value={findOptionByValue(
                                  courseOptions,
                                  tempFilters.course_id?.toString()
                                )}
                                onChange={(value) => {
                                  handleFilterChange(
                                    "course_id",
                                    value,
                                    courses
                                  );
                                }}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Level</label>
                              <CommonSelect
                                className="select"
                                options={stageOptions}
                                value={findOptionByValue(
                                  stageOptions,
                                  tempFilters.stage_id?.toString()
                                )}
                                onChange={(value) =>
                                  handleFilterChange("stage_id", value)
                                }
                                isDisabled={!tempFilters.course_id}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Batch</label>
                              <CommonSelect
                                className="select"
                                options={batchOptions}
                                value={findOptionByValue(
                                  batchOptions,
                                  tempFilters.batch_id?.toString()
                                )}
                                onChange={(value) =>
                                  handleFilterChange("batch_id", value)
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Status</label>
                              <CommonSelect
                                className="select"
                                options={statusOptions}
                                value={findOptionByValue(
                                  statusOptions,
                                  tempFilters.status
                                )}
                                onChange={(value) =>
                                  handleFilterChange("status", value)
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <Link
                          to="#"
                          className="btn btn-light me-3"
                          onClick={() => {
                            handleResetFilters();
                            setPageNumber(1);
                          }}
                        >
                          Reset
                        </Link>
                        <Link
                          to="#"
                          className="btn btn-primary"
                          onClick={(e) => {
                            handleApplyFilters();
                            setPageNumber(1);
                            if (dropdownMenuRef.current) {
                              dropdownMenuRef.current.classList.remove(
                                "show"
                              );
                            }
                          }}
                        >
                          Apply
                        </Link>
                      </div>
                    </form>
                  </div>
                </div>

              </div>
            </div>
            {/* Main attendance table */}

            <div className="card-body p-0 py-3">
              {isLoading ? (
                <div className="text-center">Loading attendance data...</div>
              ) : (
                <Table
                  dataSource={students}
                  columns={columns}
                  Selection={true}
                  onSelectionChange={setSelectedStudentKeys}
                  onSearchChange={handleSearchChange}
                  rowKey="key"
                  selectedKeys={selectedStudentKeys}
                  getCheckboxProps={getCheckboxProps}
                  pagination={{
                    current: pageNumber,
                    pageSize: recordsPerPage,
                    total: paginationData.totalItems || 0,
                    onChange: handlePageChange,
                    pageSizeOptions: [10, 20, 30],
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentAttendance;
